import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { loadListRequest } from "@/lib/listRequest";
import { useAuth } from "@/context/AuthContext";
import { useActiveLocation } from "@/context/ActiveLocationContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Boxes, Search, AlertTriangle, Clock, XCircle, CheckCircle2, Layers, MapPin, ChevronDown, SlidersHorizontal, Loader2, AlertOctagon } from "lucide-react";
import Pagination from "@/components/Pagination";
import { useSort } from "@/lib/sort";
import { SortTh } from "@/components/SortTh";
import { useTablePrefs } from "@/lib/useTablePrefs";
import { ColumnToggle } from "@/components/ColumnToggle";
import { EmptyState } from "@/components/EmptyState";

const FILTERS = [
  { val: "", icon: Layers, label: "Tümü" },
  { val: "in_stock", icon: CheckCircle2, label: "Stokta" },
  { val: "low_stock", icon: AlertTriangle, label: "Düşük Stok" },
  { val: "out", icon: XCircle, label: "Tükenen" },
  { val: "negative", icon: AlertOctagon, label: "Negatif" },
  { val: "expiring", icon: Clock, label: "SKT Yaklaşan" },
];

const INV_COLS = [
  { key: "name", label: "Ürün", locked: true },
  { key: "category", label: "Kategori" },
  { key: "stock", label: "Kullanılabilir", locked: true },
  { key: "expiry", label: "SKT" },
  { key: "lots", label: "Lot" },
];

export default function Inventory() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedFilter = searchParams.get("filter") || "";
  const filter = FILTERS.some((f) => f.val === requestedFilter) ? requestedFilter : "";
  const { isPrivileged, user } = useAuth();
  const { activeLocId, setActive } = useActiveLocation();
  const { sortKey, sortDir, toggle: onSort, sortRows } = useSort();
  const { visible, density, toggle: toggleCol, setDensity, reset: resetCols } = useTablePrefs("inventory", INV_COLS);
  const invColCount = INV_COLS.filter((c) => visible[c.key]).length;
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({ items: [], location_name: "", count: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const requestSeq = useRef(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [thr, setThr] = useState(30);
  const [thrEdit, setThrEdit] = useState("");
  const [savingThr, setSavingThr] = useState(false);
  const [unitGroups, setUnitGroups] = useState([]);
  const [unitEdit, setUnitEdit] = useState({});
  const [unitDialog, setUnitDialog] = useState(false);
  const [savingUnits, setSavingUnits] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    api.get("/settings/low-stock-threshold").then((r) => {
      setThr(r.data.threshold); setThrEdit(String(r.data.threshold));
      setUnitGroups(r.data.unit_groups || []);
      const uo = r.data.unit_overrides || {};
      setUnitEdit(Object.fromEntries((r.data.unit_groups || []).map((g) => [g.key, uo[g.key] != null ? String(uo[g.key]) : ""])));
    }).catch(() => {});
    api.get("/categories?status=active").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const saveUnits = async () => {
    setSavingUnits(true);
    try {
      const overrides = {};
      Object.entries(unitEdit).forEach(([k, v]) => { const n = Number(v); if (v !== "" && n > 0) overrides[k] = n; });
      const r = await api.put("/settings/low-stock-threshold", { threshold: thr, unit_overrides: overrides });
      const uo = r.data.unit_overrides || {};
      setUnitEdit(Object.fromEntries(unitGroups.map((g) => [g.key, uo[g.key] != null ? String(uo[g.key]) : ""])));
      toast.success("Birim bazlı stok eşikleri kaydedildi");
      setUnitDialog(false);
      load();
    } catch (e) { toast.error("Kaydedilemedi — yalnızca Yönetici/Asistan değiştirebilir"); }
    finally { setSavingUnits(false); }
  };

  const saveThr = async () => {
    const v = Number(thrEdit);
    if (!v || v < 1) return;
    setSavingThr(true);
    try {
      const r = await api.put("/settings/low-stock-threshold", { threshold: v });
      setThr(r.data.threshold); setThrEdit(String(r.data.threshold));
      load();
    } catch (e) { /* yetki yok vb. */ }
    finally { setSavingThr(false); }
  };

  useEffect(() => {
    if (isPrivileged) api.get("/locations").then((r) => {
      setLocations(r.data);
      setLocationId((c) => c || activeLocId || r.data.find((l) => (l.types || [l.type]).includes("store"))?.id || r.data[0]?.id || "");
    }).catch(() => {});
  }, [isPrivileged, activeLocId]);

  // G10 — üst bardan aktif lokasyon değişince stok görünümü anında geçsin
  useEffect(() => { if (isPrivileged && activeLocId) setLocationId(activeLocId); }, [activeLocId, isPrivileged]);

  const load = useCallback(() => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setLoadError(false);
    const params = new URLSearchParams();
    if (locationId) params.set("location_id", locationId);
    if (q) params.set("q", q);
    if (filter) params.set("filter", filter);
    if (category) params.set("category", category);
    loadListRequest(api.get(`/inventory?${params}`),
      (result) => { if (seq === requestSeq.current) setData(result); },
      () => { if (seq === requestSeq.current) setLoadError(true); }
    ).finally(() => { if (seq === requestSeq.current) setLoading(false); });
  }, [locationId, q, filter, category]);

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  useEffect(() => { setPage(1); setConfirmReset(false); }, [q, filter, locationId, pageSize, category]);
  const sortedItems = sortRows(data.items, {
    name: (it) => it.name || "",
    category: (it) => it.category || "",
    stock: (it) => Number(it.stock_on_hand) || 0,
    expiry: (it) => (it.expiry_date ? String(it.expiry_date) : ""),
    lots: (it) => (it.lots || []).filter((l) => (l.remaining || 0) > 0).length,
  });
  const paged = sortedItems.slice((page - 1) * pageSize, page * pageSize);

  const stockCls = (s) => (s <= 0 ? "text-red-600" : s <= thr ? "text-amber-600" : "text-slate-800");

  const resetNegatives = async () => {
    setResetting(true);
    try {
      const { data: res } = await api.post("/inventory/reset-negatives", locationId ? { location_id: locationId } : {});
      toast.success(`${res.fixed || 0} negatif kalem 0'a çekildi (sayım düzeltmesi kaydedildi)`);
      setConfirmReset(false);
      load();
    } catch {
      toast.error("Toplu düzeltme başarısız — yetkiniz olmayabilir");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="inventory-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Boxes className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Stok</p><h1 className="font-heading text-2xl font-bold text-slate-900">Lokasyon Stoğu</h1></div>
        </div>
        {isPrivileged ? (
          <div className="relative">
            <MapPin className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-green" />
            <select data-testid="inventory-location" value={locationId} onChange={(e) => { setLocationId(e.target.value); if (e.target.value) setActive(e.target.value); }}
              className="h-10 appearance-none rounded-md border border-input bg-white pl-8 pr-8 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-green">
              <option value="" label="Tüm Lokasyonlar (toplam)" />
              {locations.filter((l) => l.is_active !== false).map((l) => <option key={l.id} value={l.id} label={`${l.name} (${l.type_label})`} />)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </div>
        ) : (
          <div data-testid="inventory-location-fixed" className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-2 text-sm font-medium text-slate-700 ring-1 ring-inset ring-slate-200">
            <MapPin className="h-4 w-4 text-brand-green" /> {user?.location_name || "Lokasyonunuz"}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="inventory-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Ürün, SKU veya barkod ara..." className="pl-9" />
        </div>
        <div className="relative">
          <Layers className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-green" />
          <select data-testid="inventory-category" value={category} onChange={(e) => setCategory(e.target.value)}
            className="h-10 appearance-none rounded-md border border-input bg-white pl-8 pr-8 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-green">
            <option value="" label="Tüm Kategoriler" />
            {categories.map((c) => <option key={c.id || c.name} value={c.name} label={c.name} />)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        </div>
        <ColumnToggle testid="inv-table-tools" cols={INV_COLS} visible={visible} onToggle={toggleCol} density={density} onDensity={setDensity} onReset={resetCols} />
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.val} data-testid={`inv-filter-${f.val || "all"}`} onClick={() => {
            if (f.val === filter && requestedFilter === filter) return;
            const next = new URLSearchParams(searchParams);
            if (f.val) next.set("filter", f.val);
            else next.delete("filter");
            setSearchParams(next);
          }}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === f.val ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"
            }`}>
            <f.icon className="h-3.5 w-3.5" /> {f.label}
          </button>
        ))}
        <span className="self-center text-xs text-slate-400 tabular">{!loading && !loadError ? data.count : "—"} kalem</span>
        {isPrivileged && (
          <div data-testid="inv-threshold-box" className="ml-auto flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs text-slate-500 ring-1 ring-inset ring-slate-200">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            <span>Düşük stok eşiği:</span>
            <input data-testid="inv-threshold-input" type="number" min="1" value={thrEdit}
              onChange={(e) => setThrEdit(e.target.value)}
              className="h-6 w-16 rounded border border-slate-200 px-1.5 text-center tabular focus:outline-none focus:ring-1 focus:ring-brand-green" />
            <span className="text-slate-400">adet (genel)</span>
            {String(thr) !== thrEdit && (
              <button data-testid="inv-threshold-save" onClick={saveThr} disabled={savingThr}
                className="rounded bg-brand-green px-2 py-0.5 text-[11px] font-bold text-white hover:bg-emerald-700 disabled:opacity-50">Kaydet</button>
            )}
            <button data-testid="inv-unit-thresholds-btn" onClick={() => setUnitDialog(true)} title="Birim bazlı eşikler (adet/kilo/koli/kasa)"
              className="ml-1 inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50">
              <SlidersHorizontal className="h-3 w-3" /> Birim Eşikleri
            </button>
          </div>
        )}
      </div>

      {filter === "negative" && !loading && !loadError && data.items.length > 0 && (
        <div data-testid="inv-negative-bulk-bar" className="flex flex-wrap items-center gap-2 rounded-lg bg-red-50 px-3 py-2 ring-1 ring-inset ring-red-200">
          <AlertOctagon className="h-4 w-4 shrink-0 text-red-600" />
          <span className="text-xs font-medium text-red-700">{data.count} kalemde negatif stok var — muhtemel veri hatası. Sayımla düzeltin veya toplu sıfırlayın.</span>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="inv-negative-quickcount" onClick={() => navigate("/sayim")}
              className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-200 transition-colors hover:bg-red-100">
              <Boxes className="h-3.5 w-3.5" /> Hızlı Sayım
            </button>
            {!confirmReset ? (
              <button data-testid="inv-negative-reset" onClick={() => setConfirmReset(true)} disabled={!isPrivileged}
                title={isPrivileged ? "Tüm negatif stokları 0'a çek" : "Bu işlem için yetkiniz yok"}
                className="inline-flex items-center gap-1 rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50">
                Tümünü Sıfırla
              </button>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <span className="text-xs font-semibold text-red-700">Emin misiniz?</span>
                <button data-testid="inv-negative-reset-confirm" onClick={resetNegatives} disabled={resetting}
                  className="rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-50">
                  {resetting ? "…" : "Evet, Sıfırla"}
                </button>
                <button data-testid="inv-negative-reset-cancel" onClick={() => setConfirmReset(false)}
                  className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 ring-1 ring-inset ring-slate-200 transition-colors hover:bg-slate-50">
                  İptal
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      {isPrivileged && (
        <Dialog open={unitDialog} onOpenChange={setUnitDialog}>
          <DialogContent className="max-w-md" data-testid="inv-unit-dialog">
            <DialogHeader>
              <DialogTitle>Birim Bazlı Düşük Stok Eşiği</DialogTitle>
              <DialogDescription>
                Birime göre farklı eşik girin (ör. kilo ürünler için 15 kg, koli için 5). Öncelik: Ürün &gt; Kategori &gt; Birim &gt; Genel ({thr}). Boş bırakılan birim genel eşiği kullanır.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {unitGroups.map((g) => (
                <div key={g.key} className="flex items-center justify-between gap-3">
                  <label className="text-sm font-medium text-slate-700">{g.label}</label>
                  <input data-testid={`inv-unit-thr-${g.key}`} type="number" min="1"
                    value={unitEdit[g.key] ?? ""} onChange={(e) => setUnitEdit((u) => ({ ...u, [g.key]: e.target.value }))}
                    placeholder={`Genel (${thr})`}
                    className="h-9 w-32 rounded-md border border-input bg-white px-2 text-right text-sm tabular focus:outline-none focus:ring-2 focus:ring-brand-green" />
                </div>
              ))}
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setUnitDialog(false)}>İptal</Button>
              <Button data-testid="inv-unit-save" size="sm" onClick={saveUnits} disabled={savingUnits}
                className="bg-brand-green text-white hover:bg-brand-green-600">{savingUnits ? "Kaydediliyor…" : "Kaydet"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <div className="max-h-[70vh] overflow-auto rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table data-density={density} className="data-table w-full text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs text-slate-500 shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
            <tr>
              {visible.name && <SortTh label="Ürün" k="name" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="inv-sort-name" />}
              {visible.category && <SortTh label="Kategori" k="category" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="inv-sort-category" />}
              {visible.stock && <SortTh label="Kullanılabilir" k="stock" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="inv-sort-stock" />}
              {visible.expiry && <SortTh label="SKT" k="expiry" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="inv-sort-expiry" />}
              {visible.lots && <SortTh label="Lot" k="lots" align="right" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="inv-sort-lots" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={invColCount} className="px-4 py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
            ) : loadError ? (
              <tr><td colSpan={invColCount} className="px-4 py-8 text-center">
                <p role="alert" className="text-sm text-red-700">Stok verileri yüklenemedi. Bağlantıyı kontrol edip yeniden deneyin.</p>
                <Button data-testid="inv-retry" variant="outline" size="sm" className="mt-3" onClick={load}>Yeniden Dene</Button>
              </td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={invColCount} className="p-0"><EmptyState icon={Boxes} testid="inv-empty" title="Bu lokasyonda kayıt bulunamadı" hint="Farklı bir lokasyon, kategori veya filtre seçmeyi deneyin." /></td></tr>
            ) : paged.map((it) => (
              <tr key={it.product_id} data-testid={`inv-row-${it.product_id}`} className="hover:bg-slate-50">
                {visible.name && (
                <td className="px-4 py-3">
                  <p className="font-medium text-slate-800">{it.name}</p>
                  <p className="font-mono text-[10px] text-slate-400">{it.sku}</p>
                </td>)}
                {visible.category && <td className="px-4 py-3 text-slate-500">{it.category || "—"}</td>}
                {visible.stock && (
                <td className={`px-4 py-3 text-right tabular font-semibold ${stockCls(it.stock_on_hand)}`}>
                  {it.stock_on_hand} <span className="text-[10px] font-normal text-slate-400">{it.base_unit}</span>
                  {it.physical != null && it.physical !== it.available && (
                    <span className="ml-1 text-[10px] font-normal text-slate-400" title="Fiziksel stok (rezerve/karantina/SKT geçmiş hariç kullanılabilir gösterilir)">/ {it.physical} fiziksel</span>
                  )}
                  {it.low_stock_threshold != null && it.low_stock_threshold !== thr && (
                    <span className="ml-1 text-[10px] font-normal text-slate-400" title="Bu ürün/kategori için özel eşik">(eşik {it.low_stock_threshold})</span>
                  )}
                  {Number(it.stock_on_hand) < 0 && (
                    <div className="mt-1 flex items-center justify-end gap-1.5">
                      <span data-testid={`inv-negative-badge-${it.product_id}`} className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">
                        <AlertOctagon className="h-3 w-3" /> Negatif
                      </span>
                      <button data-testid={`inv-fix-count-${it.product_id}`}
                        onClick={() => { toast.info(`${it.name} için sayım başlatın — negatif stok düzeltilecek`); navigate("/sayim"); }}
                        className="rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-semibold text-white transition-colors hover:bg-red-700">
                        Sayımla Düzelt
                      </button>
                    </div>
                  )}
                </td>)}
                {visible.expiry && <td className="px-4 py-3 text-slate-500">{it.expiry_date ? String(it.expiry_date).slice(0, 10) : "—"}</td>}
                {visible.lots && <td className="px-4 py-3 text-right text-slate-500 tabular">{(it.lots || []).filter((l) => (l.remaining || 0) > 0).length}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && !loadError && data.items.length > 0 && (
        <Pagination page={page} pageSize={pageSize} total={data.items.length} onPage={setPage} onPageSize={setPageSize} testid="inventory-pagination" />
      )}
    </div>
  );
}
